import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  Skeleton,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useGetInfiniteRecipeList } from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';
import { Fragment, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { RecipeCard } from './RecipeCard';

type InfiniteScrollRecipeListProps = {
  title?: string;
  keyword?: string;
  category?: string;
  tags?: string[];
  handleReset?: () => void;
};

export function InfiniteScrollRecipeList({
  title,
  keyword,
  category,
  tags,
  handleReset,
}: InfiniteScrollRecipeListProps) {
  const { ref: loadMoreTriggerRef, inView } = useInView();
  const router = useRouter();
  const {
    data: recipes,
    isPending,
    isError,
    // error intentionally unused for user-friendly copy
    refetch,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetInfiniteRecipeList({
    sort_by: 'view',
    search: keyword,
    category,
    tagID: tags,
  });

  useEffect(() => {
    if (inView && !isError) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, isError]);

  const hasData = Boolean(recipes?.pages && recipes.pages[0]?.data?.length > 0);

  const renderGridContent = () => {
    if (isPending) {
      return (
        <Fragment>
          <Skeleton
            w="full"
            h="192px"
            rounded="lg"
            data-testid="recipe-skeleton"
          />
          <Skeleton
            w="full"
            h="192px"
            rounded="lg"
            data-testid="recipe-skeleton"
          />
          <Skeleton
            w="full"
            h="192px"
            rounded="lg"
            data-testid="recipe-skeleton"
          />
          <Skeleton
            w="full"
            h="192px"
            rounded="lg"
            data-testid="recipe-skeleton"
          />
        </Fragment>
      );
    }

    if (isError && !hasData) {
      return (
        <GridItem
          as={Center}
          display="flex"
          flexDirection="column"
          colSpan={{ base: 2, lg: 6 }}
          data-testid="error-state"
        >
          <Image
            src="/images/recipe-not-found.png"
            alt="Error Recipe"
            objectFit="cover"
            display="block"
            marginX="auto"
            height="216"
            width="216"
          />
          <Text
            maxW="md"
            textAlign="center"
            fontSize={{ base: 'sm', md: 'lg' }}
            fontWeight="semibold"
            mb={2}
          >
            Terjadi kesalahan saat memuat resep. Silakan coba lagi.
          </Text>
          <Flex gap={2}>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Coba lagi
            </Button>
            <Button size="sm" onClick={() => router.push('/')}>
              Kembali
            </Button>
          </Flex>
        </GridItem>
      );
    }

    if (hasData && recipes?.pages) {
      return recipes.pages.map((recipes, i) => (
        <Fragment key={i}>
          {recipes.data.map((recipe, index) => (
            <RecipeCard
              key={`${recipe.title}-${index}`}
              recipe={recipe}
              mb="unset"
              handleTrackEvent={(recipe) => {
                trackEvent(TRACKING_EVENTS.SELECT_RECIPE, {
                  id: recipe.id,
                  name: recipe.title,
                  brand: recipe.brands.toString(),
                  section_name: TRACKING_PAGES.RECIPE,
                });
              }}
            />
          ))}
        </Fragment>
      ));
    }

    return (
      <GridItem
        as={Center}
        display="flex"
        flexDirection="column"
        colSpan={{ base: 2, lg: 6 }}
        data-testid="empty-state"
      >
        <Image
          src="/images/recipe-not-found.png"
          alt="Empty Recipe"
          objectFit="cover"
          display="block"
          marginX="auto"
          height="216"
          width="216"
        />
        <Text
          maxW="md"
          textAlign="center"
          fontSize={{ base: 'sm', md: 'lg' }}
          fontWeight="semibold"
        >
          Resep dengan bahan atau produk yang dicari belum tersedia. Silakan
          coba dengan kata lain.
        </Text>
      </GridItem>
    );
  };

  return (
    <Box>
      {title && (
        <Heading
          mb={4}
          fontSize={{ base: 'lg', md: 'xl' }}
          fontWeight="semibold"
        >
          {title}
        </Heading>
      )}

      {keyword && (
        <Flex mb={4} justifyContent="space-between" alignItems="center">
          <Heading fontSize={{ base: 'sm', md: 'lg' }} fontWeight="semibold">
            {(recipes?.pages[0]?.data?.length || 0) +
              " Resep '" +
              keyword +
              "'"}
            <span style={{ fontWeight: 'normal' }}> Ditemukan</span>
          </Heading>

          <Button
            fontSize={{ base: 'xs', md: 'sm' }}
            variant="ghost"
            bg="transparent"
            onClick={handleReset}
          >
            Hapus Pencarian
          </Button>
        </Flex>
      )}

      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
        gap={{ base: 2, lg: 6 }}
      >
        {renderGridContent()}
      </Grid>

      <Box
        ref={loadMoreTriggerRef}
        h="100px"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {isFetchingNextPage && <Spinner size="xl" />}
      </Box>
    </Box>
  );
}
